// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { buildEvent, formatAmount, EXPENSE_TYPES, INCOME_TYPES } from '../../lib/eventLog.js';
import { formatDate } from '../../lib/formatLocale.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import type { BudgetPeriodValue, BudgetUpdatedPayload } from '../../types.js';

interface BudgetEditorProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

export function BudgetEditor({ worker, keyData }: BudgetEditorProps) {
  const { t } = useTranslation(['budgets', 'common', 'accounting', 'reports', 'errors']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const budget = id ? ledger.budgets.get(id) : undefined;

  const budgetInit = budget;
  const [editValues, setEditValues] = useState<Record<string, Record<number, string>>>(() => {
    if (!budgetInit) return {};
    const map: Record<string, Record<number, string>> = {};
    for (const v of budgetInit.values) {
      const existing = map[v.accountId] ?? {};
      map[v.accountId] = { ...existing, [v.period]: (v.amount / 100).toFixed(2) };
    }
    return map;
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const accounts = [...ledger.accounts.values()].filter(
    (a) => !a.placeholder && (EXPENSE_TYPES.has(a.type) || INCOME_TYPES.has(a.type)),
  );

  if (!budget) {
    return (
      <div className="flex-1 flex items-center justify-center text-text-muted">
        <p>{t('editor.notFound')} <button onClick={() => navigate('/budgets')} className="text-accent hover:underline">{t('editor.goBack')}</button></p>
      </div>
    );
  }

  const b = budget;
  const periods = Array.from({ length: b.numPeriods }, (_, i) => i + 1);

  function getPeriodLabel(period: number): string {
    if (b.periodType === 'monthly') {
      const startYear = parseInt(b.startDate.slice(0, 4), 10);
      const startMonth = parseInt(b.startDate.slice(5, 7), 10) - 1;
      const d = new Date(startYear, startMonth + period - 1, 1);
      return formatDate(d, { month: 'short', year: '2-digit' });
    }
    if (b.periodType === 'quarterly') return t('reports:budgetPeriods.quarter', { n: period });
    return t('reports:budgetPeriods.year', { n: period });
  }

  function getEditValue(accountId: string, period: number): string {
    return editValues[accountId]?.[period] ?? '';
  }

  function setEditValue(accountId: string, period: number, value: string) {
    setEditValues((prev) => ({
      ...prev,
      [accountId]: { ...(prev[accountId] ?? {}), [period]: value },
    }));
  }

  function getColumnTotal(period: number): number {
    return accounts.reduce((sum, a) => {
      const v = editValues[a.id]?.[period];
      return sum + (v ? Math.round(parseFloat(v) * 100) : 0);
    }, 0);
  }

  function getRowTotal(accountId: string): number {
    return periods.reduce((sum, p) => {
      const v = editValues[accountId]?.[p];
      return sum + (v ? Math.round(parseFloat(v) * 100) : 0);
    }, 0);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const values: BudgetPeriodValue[] = [];
      for (const [accountId, periodMap] of Object.entries(editValues)) {
        for (const [periodStr, valueStr] of Object.entries(periodMap)) {
          const amount = Math.round(parseFloat(valueStr) * 100);
          if (!isNaN(amount) && amount !== 0) {
            values.push({ accountId, period: parseInt(periodStr, 10), amount });
          }
        }
      }
      const payload: BudgetUpdatedPayload = { budgetId: b.id, values };
      await encryptAndPush(await buildEvent('BudgetUpdated', payload, ledger.lastSequence + 1));
      navigate('/budgets');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors:saveBudget'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={() => navigate('/budgets')} className="text-accent hover:underline text-sm mb-1 block">
            ← {t('editor.backToBudgets')}
          </button>
          <h2 className="text-md font-semibold text-text">{b.name}</h2>
          <p className="text-xs text-text-muted">{t('editor.budgetLabel', { periodType: b.periodType })} · {t('manager.periodCount', { count: periods.length })}</p>
        </div>
        <div className="flex items-center gap-3">
          {error && <span className="text-sm text-negative">{error}</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
          >
            {saving ? t('common:actions.saving') : t('saveBudget')}
          </button>
        </div>
      </div>

      <div className="bg-bg-surface border border-border overflow-auto">
        <table className="text-sm">
          <thead className="border-b border-border-strong sticky top-0 bg-bg-surface">
            <tr>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4 min-w-40">{t('accounting:columns.account')}</th>
              {periods.map((p) => (
                <th key={p} className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2 min-w-24">
                  {getPeriodLabel(p)}
                </th>
              ))}
              <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4 min-w-24">{t('total')}</th>
            </tr>
          </thead>
          <tbody>
            {accounts.filter((a) => INCOME_TYPES.has(a.type)).length > 0 && (
              <tr className="bg-bg-elevated">
                <td colSpan={periods.length + 2} className="py-1 px-4 text-xs font-semibold text-positive uppercase tracking-wide">
                  {t('reports:sections.income')}
                </td>
              </tr>
            )}
            {accounts.filter((a) => INCOME_TYPES.has(a.type)).map((account) => (
              <tr key={account.id} className="border-b border-border hover:bg-bg-elevated">
                <td className="py-1.5 px-4 text-text">{account.name}</td>
                {periods.map((p) => (
                  <td key={p} className="py-1 px-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={getEditValue(account.id, p)}
                      onChange={(e) => setEditValue(account.id, p, e.target.value)}
                      className="w-24 bg-bg border border-border text-text px-2 py-0.5 text-right text-sm font-mono focus:border-accent focus:outline-none"
                      placeholder="0.00"
                    />
                  </td>
                ))}
                <td className="py-1.5 px-4 text-right font-mono text-text-muted">
                  {formatAmount(getRowTotal(account.id))}
                </td>
              </tr>
            ))}

            {accounts.filter((a) => EXPENSE_TYPES.has(a.type)).length > 0 && (
              <tr className="bg-bg-elevated">
                <td colSpan={periods.length + 2} className="py-1 px-4 text-xs font-semibold text-negative uppercase tracking-wide">
                  {t('reports:sections.expenses')}
                </td>
              </tr>
            )}
            {accounts.filter((a) => EXPENSE_TYPES.has(a.type)).map((account) => (
              <tr key={account.id} className="border-b border-border hover:bg-bg-elevated">
                <td className="py-1.5 px-4 text-text">{account.name}</td>
                {periods.map((p) => (
                  <td key={p} className="py-1 px-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={getEditValue(account.id, p)}
                      onChange={(e) => setEditValue(account.id, p, e.target.value)}
                      className="w-24 bg-bg border border-border text-text px-2 py-0.5 text-right text-sm font-mono focus:border-accent focus:outline-none"
                      placeholder="0.00"
                    />
                  </td>
                ))}
                <td className="py-1.5 px-4 text-right font-mono text-text-muted">
                  {formatAmount(getRowTotal(account.id))}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-border-strong bg-bg-elevated">
            <tr>
              <td className="py-1.5 px-4 text-sm font-semibold text-text">{t('total')}</td>
              {periods.map((p) => (
                <td key={p} className="py-1.5 px-4 text-right font-mono font-semibold text-text text-sm">
                  {formatAmount(getColumnTotal(p))}
                </td>
              ))}
              <td className="py-1.5 px-4 text-right font-mono font-semibold text-text text-sm">
                {formatAmount(periods.reduce((sum, p) => sum + getColumnTotal(p), 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
